import React, {PureComponent, Fragment} from 'react';
import PropTypes from 'prop-types';
import {Router, Route, IndexRoute, IndexRedirect, Redirect} from 'react-router';
import PathNotFound from './lib/PathNotFound';
import ProjectViewContainer from './project-view/ProjectViewContainer';
import IssuesPanelWrapper from './document-view/IssuesPanelWrapper';
import IssuePreviewPanelContainer from './document-view/IssuePreviewPanelContainer';
import IssueNewPanelContainer from './document-view/IssueNewPanelContainer';
import IssueDetailsModalViewContainer from './issue-details-modal-view/IssueDetailsModalViewContainer';
import IssuesAdminContainer from './admin-view/IssuesAdminContainer';
import Loader from '@adsk/bim360-matrix-react-loader';
import {historyAttributesType, issueModelType} from '../constants/proptype-validation';
import {SUPPORTED_ISSUE_MODEL_TYPES, TYPE_QUALITY_ISSUES} from '../constants/issue-types';
import {AdminPageIds, AdminPageTabIds} from '../constants/issues-admin';
import {viewTypes} from '../constants/view-types';
import {
  getAnalyticsMandatoryPeopleProps,
  registerAnalyticsSuperPropsHelper,
  getAnalyticsPeopleProps
} from '../utils/analytics-helpers';
import {getIssuesUIQueryParamsFromUrl, getLocationObjectFromUrl} from '../utils/query-helpers';
import {PROJECT_VIEW_TABS} from '../constants/navigation';
import {COLLECTION_TYPES} from '../constants/issue-query-params';

const UNHANDLED_PATH = 'unhandled-path';

class Routes extends PureComponent {
  constructor(...args) {
    super(...args);

    this.state = {
      didFetchFieldIssuesMetadata: false
    };
    /*
     * Routes are defined, here, because react-router routes must be immutable -- they cannot re-render in response to
     * changing props.  (otherwise, the following error is thrown: "You cannot change <Router routes>; it will be ignored")
     */
    this.routes = this.getRoutes();

    this.setPackageDeepLinkingFalse = this.setPackageDeepLinkingFalse.bind(this);
    this.handleBrowserNavigate = this.handleBrowserNavigate.bind(this);
    this.setQueryParamsFromRoutes = this.setQueryParamsFromRoutes.bind(this);
  }

  componentWillMount() {
    const {
      setAnalyticsId,
      registerAnalyticsMandatorySuperProps,
      fetchCurrentDocsProjectAction,
      currentProject,
      region,
      analyticsId,
      analyticsHostingAppName,
      isLoadingCurrentUserPermissions
    } = this.props;

    setAnalyticsId(analyticsId);
    registerAnalyticsMandatorySuperProps(
      registerAnalyticsSuperPropsHelper(currentProject, region, analyticsHostingAppName)
    );
    fetchCurrentDocsProjectAction();
    if (!isLoadingCurrentUserPermissions) {
      this.analyticsPeopleSetup(this.props);
    }

    window.addEventListener('popstate', this.handleBrowserNavigate);
  }

  componentDidMount() {
    window.addEventListener('click', this.setPackageDeepLinkingFalse);
    window.addEventListener('keydown', this.setPackageDeepLinkingFalse);
    window.addEventListener('touchstart', this.setPackageDeepLinkingFalse);
    this.deepLinkingHandlersAttached = true;
  }

  componentWillReceiveProps(nextProps) {
    if (
      !nextProps.isLoadingCurrentUserPermissions &&
      (!this.props.oxygenMappings.users[this.props.currentUserId] &&
        nextProps.oxygenMappings.users[nextProps.currentUserId])
    ) {
      this.analyticsPeopleSetup(nextProps);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handleBrowserNavigate);
    this.removeDeepLinkingEventListeners();
  }

  setPackageDeepLinkingFalse() {
    this.props.setPackageDeepLinkingFalse();
    this.removeDeepLinkingEventListeners();
  }

  removeDeepLinkingEventListeners() {
    if (this.deepLinkingHandlersAttached) {
      window.removeEventListener('click', this.setPackageDeepLinkingFalse);
      window.removeEventListener('keydown', this.setPackageDeepLinkingFalse);
      window.removeEventListener('touchstart', this.setPackageDeepLinkingFalse);
      this.deepLinkingHandlersAttached = false;
    }
  }

  setQueryParamsFromRoutes(issueType = TYPE_QUALITY_ISSUES, currentView = this.props.currentView) {
    const location = getLocationObjectFromUrl();
    const collectionType = location.pathname.includes('issues/templates') ? COLLECTION_TYPES.TEMPLATES : issueType;
    this.props.setQueryParamsFromUrlAction(
      getIssuesUIQueryParamsFromUrl(),
      issueType,
      currentView,
      location,
      collectionType
    );
  }

  handleBrowserNavigate() {
    this.setQueryParamsFromRoutes();
  }

  analyticsPeopleSetup(props) {
    const {oxygenMappings} = this.props;

    if (props.currentUserId) {
      const user = oxygenMappings.users[props.currentUserId];
      this.props.setAnalyticsMandatoryPeopleProps(getAnalyticsMandatoryPeopleProps(user));
      this.props.setAnalyticsPeopleProps(getAnalyticsPeopleProps(user));
    }
  }

  handleOnRouteChange() {
    if (!this.router) {
      return;
    }

    const {routeChangeEventTriggerAction} = this.props;
    const {location, params} = this.router.state;
    routeChangeEventTriggerAction(location.pathname, params);
  }

  setIssueType(nextState, replace) {
    const {
      params: {issueType}
    } = nextState;
    if (SUPPORTED_ISSUE_MODEL_TYPES.includes(issueType)) {
      return issueType;
    } else {
      replace({
        pathname: UNHANDLED_PATH,
        state: {
          originalPathname: nextState.location.pathname
        }
      });
    }

    return null;
  }

  fetchIssueById(id, issueType) {
    const {fetchIssueInstanceAction, isPackageDeepLinking} = this.props;
    if (SUPPORTED_ISSUE_MODEL_TYPES.includes(issueType)) {
      fetchIssueInstanceAction(id, isPackageDeepLinking);
    }
  }

  fetchAllIssuesTypes() {
    const {fetchIssuesAction} = this.props;
    this.loadFieldIssueMetadata();
    fetchIssuesAction({type: TYPE_QUALITY_ISSUES});
  }

  loadFieldIssueMetadata() {
    const {getDefaultLocationTree} = this.props;

    if (!this.state.didFetchFieldIssuesMetadata) {
      this.loadFieldIssueCustomizationMetadata();
      getDefaultLocationTree();

      this.setState({didFetchFieldIssuesMetadata: true});
    }
  }

  // Loads customization related metadata that is also relevant for admin view
  loadFieldIssueCustomizationMetadata() {
    !this.props.isFieldIssuesCustomizationDataFetched && this.props.fetchIssuesCustomizationMetadata();
    !this.props.isIssueRootCausesDefinitionsFetched && this.props.fetchIssueRootCausesAction();
  }

  handleOnEnterDocumentView(nextState, replace) {
    const currentView = viewTypes.DOCUMENT;
    const {
      params: {issueType}
    } = nextState;

    // Prevent setting view type if it didn't change (happens when switching between issue types in the same view)
    if (this.props.currentView !== currentView) {
      this.props.setCurrentViewAction(currentView);
    }
    this.setIssueType(nextState, replace);
    this.setQueryParamsFromRoutes(issueType, currentView);
    this.fetchAllIssuesTypes();
    this.props.fetchIssueTemplateCategoriesAction();
    this.props.fetchActiveTemplatesAction();

    if (!this.props.currentUserId) {
      this.props.fetchCurrentUserAction();
    }
    this.props.fetchRequiredFields();
  }

  /**
   *
   * @param {*} currState
   * @param {*} nextState
   * only need to reset the preview id if navigating away from preview but not when changing from 1 pushpin to another
   */
  handleOnChangeDocumentView = (currState, nextState) => {
    if (!nextState?.params?.id) {
      this.resetPreviewId();
    }
  };

  handleOnEnterProjectView(nextState, replace) {
    const currentView = viewTypes.PROJECT;
    const {
      params: {issueType = TYPE_QUALITY_ISSUES}
    } = nextState;

    // Prevent setting view type if it didn't change (happens when switching between issue types in the same view)
    if (this.props.currentView !== currentView) {
      this.props.setCurrentViewAction(currentView);
    }
    if (issueType === TYPE_QUALITY_ISSUES) {
      this.loadFieldIssueMetadata();
    }
    this.setIssueType(nextState, replace);
    this.setQueryParamsFromRoutes(issueType, currentView);

    if (!this.props.currentUserId) {
      this.props.fetchCurrentUserAction();
    }
    this.props.fetchRequiredFields();
  }

  handleOnEnterPreviewIssue(nextState) {
    const {setPreviewIssueIdAction} = this.props;
    const {
      params: {id, issueType}
    } = nextState;
    setPreviewIssueIdAction(id);
    this.fetchIssueById(id, issueType);
  }

  handleOnEnterPreviewTemplate(nextState) {
    const {setPreviewTemplateIdAction} = this.props;
    const {
      params: {id}
    } = nextState;
    setPreviewTemplateIdAction(id);
  }

  resetPreviewTemplateId() {
    const {setPreviewTemplateIdAction} = this.props;
    setPreviewTemplateIdAction(null);
  }

  handleOnEnterFieldAdminPage() {
    if (!this.props.currentUserId) {
      this.props.fetchCurrentUserAction();
    }
    this.props.getDefaultLocationTree(); // load for requiredFields
    this.props.fetchRequiredFields();
    this.props.fetchIssuesPermissions();
    this.loadFieldIssueCustomizationMetadata();
  }

  resetPreviewId() {
    const {setPreviewIssueIdAction} = this.props;
    setPreviewIssueIdAction(null);
  }

  showNewIssueModal() {
    this.toggleNewIssueModal(true);
  }

  hideNewIssueModal() {
    this.toggleNewIssueModal(false);
  }

  toggleNewIssueModal(visible) {
    const {showNewIssueModalAction} = this.props;
    showNewIssueModalAction(visible);
  }

  showNewTemplateModal() {
    this.toggleNewTemplateModal(true);
  }

  hideNewTemplateModal() {
    this.toggleNewTemplateModal(false);
  }

  toggleNewTemplateModal(visible) {
    const {showNewTemplateModalAction} = this.props;
    showNewTemplateModalAction(visible);
  }

  showNewIssueAttributeModal() {
    this.toggleNewIssueAttributeModal(true);
  }

  hideNewIssueAttributeModal() {
    this.toggleNewIssueAttributeModal(false);
  }

  handleOnEnterIssueAttributeEditModal(nextState) {
    const {editIssueAttribute} = this.props;
    const {
      params: {id}
    } = nextState;
    editIssueAttribute(id);
    this.toggleNewIssueAttributeModal(true);
  }

  resetIssueAttributeEditModal() {
    const {resetEditingIssueAttributeId} = this.props;
    this.toggleNewIssueAttributeModal(false);
    resetEditingIssueAttributeId();
  }

  toggleNewIssueAttributeModal(visible) {
    const {showNewIssueAttributeModalAction} = this.props;
    showNewIssueAttributeModalAction(visible);
  }

  getIssuesAdminRoutes() {
    // Return IssuesAdminContainer which contains all issue admin tabs (and includes matching navigation tabs)
    return (
      <Fragment>
        <Route path="issues" onEnter={this.handleOnEnterFieldAdminPage.bind(this)}>
          <IndexRedirect to={AdminPageTabIds.ISSUES_PERMISSIONS} />
          <Route path=":adminTabId">
            <IndexRoute component={IssuesAdminContainer} />
          </Route>
          <Route
            path={`${AdminPageTabIds.ISSUE_ATTRIBUTES}/new`}
            component={IssuesAdminContainer}
            onEnter={this.showNewIssueAttributeModal.bind(this)}
            onLeave={this.hideNewIssueAttributeModal.bind(this)}
            adminTabId={AdminPageTabIds.ISSUE_ATTRIBUTES}
          />
          <Route
            path={`${AdminPageTabIds.ISSUE_ATTRIBUTES}/:id`}
            component={IssuesAdminContainer}
            onEnter={this.handleOnEnterIssueAttributeEditModal.bind(this)}
            onLeave={this.resetIssueAttributeEditModal.bind(this)}
            adminTabId={AdminPageTabIds.ISSUE_ATTRIBUTES}
          />
        </Route>

        {/* Support previous paths in order not to break ourselves (some components still rely */}
        {/* on them at the moment) or anyone who's using them */}
        <Redirect from={AdminPageIds.ISSUE_ATTRIBUTES} to={`issues/${AdminPageTabIds.ISSUE_ATTRIBUTES}`} />
        <Redirect from={`${AdminPageIds.ISSUE_ATTRIBUTES}/new`} to={`issues/${AdminPageTabIds.ISSUE_ATTRIBUTES}/new`} />
        <Redirect from={`${AdminPageIds.ISSUE_ATTRIBUTES}/:id`} to={`issues/${AdminPageTabIds.ISSUE_ATTRIBUTES}/:id`} />
        <Redirect from={AdminPageIds.ISSUE_TYPES} to={`issues/${AdminPageTabIds.ISSUE_TYPES}`} />
      </Fragment>
    );
  }

  getRoutes() {
    const {history} = this.props;
    return (
      <Router
        history={history}
        onUpdate={this.handleOnRouteChange.bind(this)}
        ref={router => {
          this.router = router;
        }}
      >
        <Route path="/">
          <IndexRoute component={PathNotFound} />
          <Route path={UNHANDLED_PATH} component={PathNotFound} />
          <Route path="admin">{this.getIssuesAdminRoutes()}</Route>
          <Route path="project/:issueType" onEnter={this.handleOnEnterProjectView.bind(this)}>
            <IndexRoute component={ProjectViewContainer} />
            <Route
              path="new"
              component={ProjectViewContainer}
              onEnter={this.showNewIssueModal.bind(this)}
              onLeave={this.hideNewIssueModal.bind(this)}
            />

            <Route path="templates">
              <IndexRoute component={ProjectViewContainer} selectedTabId={PROJECT_VIEW_TABS.TEMPLATES} />
              <Route
                path="new"
                component={ProjectViewContainer}
                selectedTabId={PROJECT_VIEW_TABS.TEMPLATES}
                onEnter={this.showNewTemplateModal.bind(this)}
                onLeave={this.hideNewTemplateModal.bind(this)}
              />
              <Route
                path=":id"
                component={ProjectViewContainer}
                selectedTabId={PROJECT_VIEW_TABS.TEMPLATES}
                onEnter={this.handleOnEnterPreviewTemplate.bind(this)}
                onLeave={this.resetPreviewTemplateId.bind(this)}
              />
            </Route>

            <Route
              path=":id"
              component={ProjectViewContainer}
              onEnter={this.handleOnEnterPreviewIssue.bind(this)}
              onLeave={this.resetPreviewId.bind(this)}
            />
          </Route>
          <Route path="issue-details-modal/:issueType" onEnter={this.handleOnEnterProjectView.bind(this)}>
            <IndexRoute component={Loader} />
            <Route
              path=":id"
              component={IssueDetailsModalViewContainer}
              onEnter={this.handleOnEnterPreviewIssue.bind(this)}
              onLeave={this.resetPreviewId.bind(this)}
            />
          </Route>
          <Route
            path=":issueType"
            onEnter={this.handleOnEnterDocumentView.bind(this)}
            onChange={this.handleOnChangeDocumentView}
          >
            <IndexRoute component={IssuesPanelWrapper} />
            <Route path="new" component={IssueNewPanelContainer} />
            <Route
              path=":id"
              component={IssuePreviewPanelContainer}
              onEnter={this.handleOnEnterPreviewIssue.bind(this)}
            />
          </Route>
        </Route>
        <Route path="*" component={PathNotFound} />
      </Router>
    );
  }

  render() {
    return this.routes;
  }
}

Routes.propTypes = {
  history: historyAttributesType.isRequired,
  routeChangeEventTriggerAction: PropTypes.func.isRequired,
  fetchIssuesAction: PropTypes.func.isRequired,
  fetchIssueInstanceAction: PropTypes.func.isRequired,
  fetchCurrentDocsProjectAction: PropTypes.func.isRequired,
  issueType: issueModelType,
  setPreviewIssueIdAction: PropTypes.func.isRequired,
  setPreviewTemplateIdAction: PropTypes.func.isRequired,
  showNewIssueModalAction: PropTypes.func.isRequired,
  showNewTemplateModalAction: PropTypes.func.isRequired,
  isIssuesEnabled: PropTypes.bool,
  isQualityIssuesEnabled: PropTypes.bool,
  fetchIssuesCustomizationMetadata: PropTypes.func.isRequired,
  getDefaultLocationTree: PropTypes.func.isRequired,
  setCurrentViewAction: PropTypes.func.isRequired,
  currentView: PropTypes.string,
  fetchCurrentUserAction: PropTypes.func.isRequired,
  fetchRequiredFields: PropTypes.func.isRequired,
  currentUserId: PropTypes.string,
  isLoadingCurrentUserPermissions: PropTypes.bool.isRequired,
  setAnalyticsId: PropTypes.func.isRequired,
  registerAnalyticsMandatorySuperProps: PropTypes.func,
  setAnalyticsMandatoryPeopleProps: PropTypes.func,
  setAnalyticsPeopleProps: PropTypes.func,
  setQueryParamsFromUrlAction: PropTypes.func.isRequired,
  currentProject: PropTypes.object,
  region: PropTypes.string,
  analyticsId: PropTypes.string,
  analyticsHostingAppName: PropTypes.string,
  oxygenMappings: PropTypes.object,
  setPackageDeepLinkingFalse: PropTypes.func.isRequired,
  showNewIssueAttributeModalAction: PropTypes.func.isRequired,
  editIssueAttribute: PropTypes.func.isRequired,
  resetEditingIssueAttributeId: PropTypes.func.isRequired,
  isPackageDeepLinking: PropTypes.bool.isRequired,
  isFieldIssuesCustomizationDataFetched: PropTypes.bool.isRequired,
  fetchIssuesPermissions: PropTypes.func,
  fetchIssueRootCausesAction: PropTypes.func,
  isIssueRootCausesDefinitionsFetched: PropTypes.bool,
  fetchIssueTemplateCategoriesAction: PropTypes.func.isRequired,
  fetchActiveTemplatesAction: PropTypes.func.isRequired
};

Routes.displayName = 'Routes';

export default Routes;
